import React, { FC } from "react";
import { MatterDetailsModal, AccruedFee, AccruedTimeLogProps } from "@design-system";
import { useFindMatterQuery } from "~/hooks/matters";
import {
  UPDATE_MATTER,
  UPDATE_PHASE,
  UPDATE_TASK,
  CREATE_OR_UPDATE_MATTER_COMPLETION_LOG,
} from "../../graphql/mutation";
import {
  FIND_CUMULATIVE_ACCRUED_FEES_FOR_MATTER,
  FIND_MATTER_COMPLETION_LOGS,
} from "../../graphql/query";
import { useQuery, useMutation } from "@apollo/client";

type MatterDetailsContainerProps = {
  matterId: number;
  show: boolean;
  onClose: () => void;
  accruedFee: AccruedFee;
} & AccruedTimeLogProps;

export const MatterDetailsContainer: FC<MatterDetailsContainerProps> = ({
  matterId,
  show,
  onClose,
  accruedFee,
  ...rest
}) => {
  const { data } = useFindMatterQuery({ id: matterId });
  const [updateMatter] = useMutation(UPDATE_MATTER);
  const [updatePhase] = useMutation(UPDATE_PHASE);
  const [updateTask] = useMutation(UPDATE_TASK);
  const [createOrUpdateMatterCompletionLog] = useMutation(CREATE_OR_UPDATE_MATTER_COMPLETION_LOG);
  const { data: cumulativeAccruedFeesData } = useQuery(FIND_CUMULATIVE_ACCRUED_FEES_FOR_MATTER, {
    variables: {
      matterId,
    },
  });
  const { data: completionLogsData } = useQuery(FIND_MATTER_COMPLETION_LOGS, {
    variables: {
      matterId,
    },
  });

  const handleTogglePhaseComplete = (phaseId: number, completed: boolean) => {
    updatePhase({
      variables: {
        phaseId,
        matterId,
        data: {
          markedComplete: {
            set: completed,
          },
        },
      },
    });
  };

  const handleToggleTaskComplete = (taskId: number, completed: boolean) => {
    updateTask({
      variables: {
        taskId,
        data: {
          markedComplete: {
            set: completed,
          },
        },
      },
    });
  };

  const handleUpdateCompletionPercentage = (percentage: number) => {
    updateMatter({
      variables: {
        id: matterId,
        data: {
          completionPercentage: {
            set: percentage,
          },
        },
      },
    });
    createOrUpdateMatterCompletionLog({
      variables: {
        matterId,
        completionPercentage: percentage,
      },
    });
  };

  const handleUpdatePhaseCompletionPercentage = (phaseId: number, percentage: number) => {
    updatePhase({
      variables: {
        phaseId,
        matterId,
        data: {
          completionPercentage: {
            set: percentage,
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: `Phase:${phaseId}`,
          fields: {
            completionPercentage: () => percentage,
          },
        });
      },
    });
  };

  const handleUpdateDeadline = (deadline: Date) => {
    updateMatter({
      variables: {
        id: matterId,
        data: {
          deadlineDate: {
            set: deadline.toISOString(),
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: `Matter:${matterId}`,
          fields: {
            deadlineDate: () => deadline.toISOString(),
          },
        });
      },
    });
  };

  const phases = data?.findMatter.phases ?? [];
  const startDate = data?.findMatter.createdAt ? new Date(data.findMatter.createdAt) : undefined;
  const deadline = data?.findMatter.deadlineDate
    ? new Date(data.findMatter.deadlineDate)
    : undefined;
  return (
    <MatterDetailsModal
      show={show}
      onClose={onClose}
      accruedFee={accruedFee}
      cumulativeAccruedFees={cumulativeAccruedFeesData?.findCumulativeAccruedFeesForMatter ?? []}
      phases={phases}
      matterCompletionPercentage={data?.findMatter.completionPercentage ?? 0}
      onTogglePhaseComplete={handleTogglePhaseComplete}
      onToggleTaskComplete={handleToggleTaskComplete}
      onCompletionPercentageChange={handleUpdateCompletionPercentage}
      onPhaseCompletionPercentageChange={handleUpdatePhaseCompletionPercentage}
      completionLogs={completionLogsData?.findMatterCompletionLogs ?? []}
      startDate={startDate}
      deadline={deadline}
      onDeadlineChange={handleUpdateDeadline}
      gpmThreshold={data?.findMatter.gpmDangerThreshold}
      {...rest}
    />
  );
};
